import React, { useState } from "react";
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Container,
  Divider,
  Stack,
  TextField,
  Typography,
} from "@mui/material";

import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import SecurityIcon from "@mui/icons-material/Security";
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import RestoreIcon from "@mui/icons-material/Restore";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import EmailOutlinedIcon from "@mui/icons-material/EmailOutlined";
import VerifiedOutlinedIcon from "@mui/icons-material/VerifiedOutlined";

import { Link } from "react-router-dom";
import AuthApi from "../utils/authApi";

const DeleteAccount = () => {
  const { requestAccountDeletion, verifyAccountDeletion } = AuthApi();
  const [email, setEmail] = useState("");
  const [otp, setOtp] = useState("");

  const [step, setStep] = useState("email");

  const [loading, setLoading] = useState(false);

  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");

  /**
   * Request deletion OTP
   */
  const handleRequestDeletion = async (event) => {
    event.preventDefault();

    setError("");
    setSuccess("");

    const normalizedEmail = email.trim().toLowerCase();

    if (!normalizedEmail) {
      setError("Please enter the email address associated with your account.");
      return;
    }

    setLoading(true);

    try {
      await requestAccountDeletion(normalizedEmail);

      setStep("otp");

      setSuccess(
        "If an account exists for this email address, a verification code has been sent to your email.",
      );
    } catch (error) {
      setError(
        error.response?.data?.message ||
          error.message ||
          "Something went wrong while requesting account deletion.",
      );
    } finally {
      setLoading(false);
    }
  };

  /**
   * Verify OTP and delete account
   */
  const handleConfirmDeletion = async (event) => {
    event.preventDefault();

    setError("");
    setSuccess("");

    const normalizedEmail = email.trim().toLowerCase();
    const normalizedOtp = otp.trim();

    if (!normalizedEmail) {
      setError("Please enter your email address.");
      return;
    }

    if (!normalizedOtp) {
      setError("Please enter the verification code.");
      return;
    }

    if (!/^\d{6}$/.test(normalizedOtp)) {
      setError("Please enter the 6-digit verification code.");
      return;
    }

    setLoading(true);

    try {
      await verifyAccountDeletion(normalizedEmail, normalizedOtp);

      setStep("success");

      setSuccess(
        "Your account has been deleted. Your account data will be securely retained for 30 days, during which you can recover your account.",
      );
    } catch (error) {
      setError(
        error.response?.data?.message ||
          error.message ||
          "Something went wrong while deleting your account.",
      );
    } finally {
      setLoading(false);
    }
  };

  /**
   * Return to email step
   */
  const handleChangeEmail = () => {
    setStep("email");
    setOtp("");
    setError("");
    setSuccess("");
  };

  return (
    <Box
      sx={{
        minHeight: "100vh",
        backgroundColor: "#f7f9fc",
        py: { xs: 4, md: 7 },
      }}
    >
      <Container maxWidth="md">
        {/* Header */}
        <Stack
          direction="row"
          alignItems="center"
          justifyContent="space-between"
          sx={{ mb: 5 }}
        >
          <Typography
            variant="h5"
            sx={{
              fontWeight: 700,
              color: "#1D629D",
            }}
          >
            PristinePV
          </Typography>

          <Button
            component={Link}
            to="/"
            startIcon={<ArrowBackIcon />}
            sx={{
              textTransform: "none",
              color: "#1D629D",
              fontWeight: 600,
            }}
          >
            Back to website
          </Button>
        </Stack>

        {/* Main Card */}
        <Card
          elevation={0}
          sx={{
            border: "1px solid #e3e8ef",
            borderRadius: 3,
            backgroundColor: "#ffffff",
          }}
        >
          <CardContent
            sx={{
              p: {
                xs: 3,
                md: 5,
              },
            }}
          >
            {/* Page Header */}
            <Stack spacing={2} alignItems="center" textAlign="center">
              <Box
                sx={{
                  width: 64,
                  height: 64,
                  borderRadius: "50%",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  backgroundColor: "#fff1f1",
                }}
              >
                <DeleteOutlineIcon
                  sx={{
                    fontSize: 34,
                    color: "#d32f2f",
                  }}
                />
              </Box>

              <Typography
                variant="h3"
                sx={{
                  fontSize: {
                    xs: "28px",
                    md: "36px",
                  },
                  fontWeight: 700,
                  color: "#1f2937",
                }}
              >
                Delete your PristinePV account
              </Typography>

              <Typography
                variant="body1"
                sx={{
                  maxWidth: 650,
                  color: "#5f6b7a",
                  lineHeight: 1.7,
                }}
              >
                You can delete your PristinePV account from the application, or
                request account deletion directly from this website.
              </Typography>
            </Stack>

            <Divider sx={{ my: 4 }} />

            {/* Option 1 */}
            <Typography
              variant="h5"
              sx={{
                fontWeight: 700,
                color: "#1f2937",
                mb: 2,
              }}
            >
              Option 1 — Delete your account in the app
            </Typography>

            <Typography
              variant="body1"
              sx={{
                color: "#4b5563",
                lineHeight: 1.8,
                mb: 2,
              }}
            >
              If you can sign in to the PristinePV application, this is the
              quickest way to request account deletion.
            </Typography>

            <Box
              component="ol"
              sx={{
                pl: 3,
                color: "#4b5563",
                "& li": {
                  mb: 1.5,
                  lineHeight: 1.7,
                },
              }}
            >
              <li>Open the PristinePV application.</li>

              <li>
                Open your <strong>Profile</strong> or{" "}
                <strong>Account Settings</strong>.
              </li>

              <li>
                Select <strong>Delete Account</strong>.
              </li>

              <li>Confirm that you want to delete your account.</li>
            </Box>

            <Divider sx={{ my: 4 }} />

            {/* Option 2 */}
            <Typography
              variant="h5"
              sx={{
                fontWeight: 700,
                color: "#1f2937",
                mb: 1,
              }}
            >
              Option 2 — Request deletion online
            </Typography>

            <Typography
              variant="body1"
              sx={{
                color: "#4b5563",
                lineHeight: 1.8,
                mb: 3,
              }}
            >
              If you cannot access the PristinePV application, you can request
              account deletion here. We will verify ownership of the account
              using a verification code sent to the registered email address.
            </Typography>

            {/* Deletion Form */}
            {step !== "success" && (
              <Box
                sx={{
                  p: {
                    xs: 2.5,
                    md: 3,
                  },
                  borderRadius: 2,
                  backgroundColor: "#fafbfc",
                  border: "1px solid #e3e8ef",
                }}
              >
                {error && (
                  <Alert
                    severity="error"
                    sx={{
                      mb: 3,
                      borderRadius: 2,
                    }}
                  >
                    {error}
                  </Alert>
                )}

                {success && step === "otp" && (
                  <Alert
                    severity="success"
                    sx={{
                      mb: 3,
                      borderRadius: 2,
                    }}
                  >
                    {success}
                  </Alert>
                )}

                {step === "email" && (
                  <Box component="form" onSubmit={handleRequestDeletion}>
                    <Stack spacing={3}>
                      <TextField
                        fullWidth
                        label="Email address"
                        type="email"
                        value={email}
                        onChange={(event) => setEmail(event.target.value)}
                        placeholder="Enter your PristinePV email"
                        autoComplete="email"
                        disabled={loading}
                        InputProps={{
                          startAdornment: (
                            <EmailOutlinedIcon
                              sx={{
                                mr: 1,
                                color: "#7a8594",
                              }}
                            />
                          ),
                        }}
                      />

                      <Button
                        type="submit"
                        variant="contained"
                        size="large"
                        fullWidth
                        disabled={loading}
                        startIcon={<EmailOutlinedIcon />}
                        sx={{
                          py: 1.4,
                          textTransform: "none",
                          fontWeight: 600,
                          fontSize: "16px",
                          backgroundColor: "#d32f2f",
                          "&:hover": {
                            backgroundColor: "#b71c1c",
                          },
                        }}
                      >
                        {loading
                          ? "Sending verification code..."
                          : "Request account deletion"}
                      </Button>
                    </Stack>
                  </Box>
                )}

                {step === "otp" && (
                  <Box component="form" onSubmit={handleConfirmDeletion}>
                    <Stack spacing={3}>
                      <Box>
                        <Typography
                          variant="body2"
                          sx={{
                            color: "#667382",
                            mb: 1,
                          }}
                        >
                          Verification code sent to
                        </Typography>

                        <Typography
                          variant="body1"
                          sx={{
                            fontWeight: 600,
                            color: "#273444",
                          }}
                        >
                          {email}
                        </Typography>
                      </Box>

                      <TextField
                        fullWidth
                        label="Verification code"
                        value={otp}
                        onChange={(event) => {
                          const value = event.target.value
                            .replace(/\D/g, "")
                            .slice(0, 6);

                          setOtp(value);
                        }}
                        placeholder="Enter 6-digit code"
                        inputProps={{
                          maxLength: 6,
                          inputMode: "numeric",
                        }}
                        disabled={loading}
                      />

                      <Alert severity="warning">
                        Confirming this code will immediately disable your
                        account. Your account will remain recoverable for 30
                        days.
                      </Alert>

                      <Button
                        type="submit"
                        variant="contained"
                        size="large"
                        fullWidth
                        disabled={loading}
                        startIcon={<VerifiedOutlinedIcon />}
                        sx={{
                          py: 1.4,
                          textTransform: "none",
                          fontWeight: 600,
                          fontSize: "16px",
                          backgroundColor: "#d32f2f",
                          "&:hover": {
                            backgroundColor: "#b71c1c",
                          },
                        }}
                      >
                        {loading
                          ? "Deleting account..."
                          : "Confirm account deletion"}
                      </Button>

                      <Button
                        type="button"
                        variant="text"
                        onClick={handleChangeEmail}
                        disabled={loading}
                        sx={{
                          textTransform: "none",
                          color: "#1D629D",
                          fontWeight: 600,
                        }}
                      >
                        Use a different email address
                      </Button>
                    </Stack>
                  </Box>
                )}
              </Box>
            )}

            {/* Successful deletion */}
            {step === "success" && (
              <Box
                sx={{
                  p: 3,
                  borderRadius: 2,
                  backgroundColor: "#f0f7fb",
                  border: "1px solid #d8eaf4",
                }}
              >
                <Stack spacing={2} alignItems="center" textAlign="center">
                  <Box
                    sx={{
                      width: 60,
                      height: 60,
                      borderRadius: "50%",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      backgroundColor: "#eaf4f9",
                    }}
                  >
                    <VerifiedOutlinedIcon
                      sx={{
                        fontSize: 32,
                        color: "#1D629D",
                      }}
                    />
                  </Box>

                  <Typography
                    variant="h5"
                    sx={{
                      fontWeight: 700,
                      color: "#1f2937",
                    }}
                  >
                    Account deleted
                  </Typography>

                  <Typography
                    variant="body1"
                    sx={{
                      color: "#4b5563",
                      lineHeight: 1.8,
                    }}
                  >
                    Your account has been deleted. Your account data will be
                    securely retained for 30 days, during which you can recover
                    your account.
                  </Typography>

                  <Typography
                    variant="body1"
                    sx={{
                      color: "#4b5563",
                      lineHeight: 1.8,
                    }}
                  >
                    After the 30-day recovery period, your account and
                    applicable personal data will be permanently deleted and
                    cannot be recovered.
                  </Typography>

                  <Button
                    component={Link}
                    to="/recover-account"
                    variant="outlined"
                    startIcon={<RestoreIcon />}
                    sx={{
                      mt: 1,
                      textTransform: "none",
                      fontWeight: 600,
                      color: "#1D629D",
                      borderColor: "#1D629D",
                    }}
                  >
                    Recover my account
                  </Button>
                </Stack>
              </Box>
            )}

            <Divider sx={{ my: 4 }} />

            {/* What happens */}
            <Typography
              variant="h5"
              sx={{
                fontWeight: 700,
                color: "#1f2937",
                mb: 3,
              }}
            >
              What happens when you delete your account?
            </Typography>

            <Stack spacing={2.5}>
              <InfoCard
                icon={<SecurityIcon />}
                title="Your access is disabled immediately"
                description="Your account is marked as deleted and inactive. Active sessions are revoked and normal sign-in is disabled."
              />

              <InfoCard
                icon={<AccessTimeIcon />}
                title="30-day recovery period"
                description="Your account data is securely retained for 30 days so that you can recover your account if you change your mind."
              />

              <InfoCard
                icon={<RestoreIcon />}
                title="Permanent deletion after 30 days"
                description="After the recovery period expires, your account and applicable personal data will be permanently deleted and cannot be recovered."
              />
            </Stack>

            <Divider sx={{ my: 4 }} />

            {/* Data retention */}
            <Typography
              variant="h5"
              sx={{
                fontWeight: 700,
                color: "#1f2937",
                mb: 2,
              }}
            >
              Data deletion and retention
            </Typography>

            <Typography
              variant="body1"
              sx={{
                color: "#4b5563",
                lineHeight: 1.8,
                mb: 2,
              }}
            >
              During the 30-day recovery period, your account information is
              retained so that your account can be restored.
            </Typography>

            <Typography
              variant="body1"
              sx={{
                color: "#4b5563",
                lineHeight: 1.8,
                mb: 2,
              }}
            >
              After the recovery period, applicable personal information
              associated with your account will be permanently deleted in
              accordance with our Privacy Policy.
            </Typography>

            <Typography
              variant="body1"
              sx={{
                color: "#4b5563",
                lineHeight: 1.8,
              }}
            >
              Certain information may be retained where required for legal,
              accounting, security, fraud-prevention, or other legitimate
              business purposes.
            </Typography>

            <Divider sx={{ my: 4 }} />

            {/* Organisation information */}
            <Typography
              variant="h5"
              sx={{
                fontWeight: 700,
                color: "#1f2937",
                mb: 2,
              }}
            >
              Organisation accounts
            </Typography>

            <Typography
              variant="body1"
              sx={{
                color: "#4b5563",
                lineHeight: 1.8,
              }}
            >
              Deleting an individual user account does not cancel an
              organisation's PristinePV subscription. Organisation-level data
              may continue to be retained according to the organisation's
              subscription, legal obligations, and our Privacy Policy.
            </Typography>

            <Divider sx={{ my: 4 }} />

            {/* Legal links */}
            <Stack
              direction={{
                xs: "column",
                sm: "row",
              }}
              spacing={1}
              justifyContent="center"
              alignItems="center"
            >
              <Button
                component={Link}
                to="/privacy-policy"
                sx={{
                  textTransform: "none",
                  fontWeight: 600,
                  color: "#1D629D",
                }}
              >
                Privacy Policy
              </Button>

              <Button
                component={Link}
                to="/terms-and-conditions"
                sx={{
                  textTransform: "none",
                  fontWeight: 600,
                  color: "#1D629D",
                }}
              >
                Terms & Conditions
              </Button>

              <Button
                component={Link}
                to="/recover-account"
                sx={{
                  textTransform: "none",
                  fontWeight: 600,
                  color: "#1D629D",
                }}
              >
                Recover Account
              </Button>
            </Stack>
          </CardContent>
        </Card>

        {/* Footer */}
        <Typography
          variant="body2"
          align="center"
          sx={{
            mt: 4,
            color: "#7a8594",
          }}
        >
          © {new Date().getFullYear()} Pristine PV Limited. All rights reserved.
        </Typography>
      </Container>
    </Box>
  );
};

const InfoCard = ({ icon, title, description }) => {
  return (
    <Box
      sx={{
        display: "flex",
        gap: 2,
        p: 2.5,
        borderRadius: 2,
        backgroundColor: "#fafbfc",
        border: "1px solid #e8edf2",
      }}
    >
      <Box
        sx={{
          minWidth: 44,
          width: 44,
          height: 44,
          borderRadius: 1.5,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          backgroundColor: "#eaf4f9",
          color: "#1D629D",
        }}
      >
        {icon}
      </Box>

      <Box>
        <Typography
          variant="subtitle1"
          sx={{
            fontWeight: 700,
            color: "#273444",
            mb: 0.5,
          }}
        >
          {title}
        </Typography>

        <Typography
          variant="body2"
          sx={{
            color: "#667382",
            lineHeight: 1.7,
          }}
        >
          {description}
        </Typography>
      </Box>
    </Box>
  );
};

export default DeleteAccount;
